import { useState, useEffect, useCallback } from 'react';
import { updateQuestion, regenerateQuestionAudio } from '@/Data/Topik';
import { QuestionGroupType } from '@/Data/Topik/types';
import EditableFileField from '@/Components/EditableFileField';

type QuestionType = QuestionGroupType['questions'][number];

type Choice = {
    id: 'A' | 'B' | 'C' | 'D';
    text: string;
    image_url?: string;
};

type EditQuestionDialogProps = {
    isOpen: boolean;
    onClose: () => void;
    question: QuestionType | null;
    isCreatingNew: boolean;
    examid: string;
    section: 'listening' | 'reading' | 'writing';
    currentGroupId: string;
    onSave: () => void;
    questionIdToGroupMapping: Record<number, string>;
    getSectionQuestionGroups: () => QuestionGroupType[];
};

function EditQuestionDialog({
    isOpen,
    onClose,
    question,
    isCreatingNew,
    examid,
    section,
    onSave,
    questionIdToGroupMapping,
    getSectionQuestionGroups
}: EditQuestionDialogProps) {
    const [questionId, setQuestionId] = useState(1);
    const [questionText, setQuestionText] = useState('');
    const [questionImage, setQuestionImage] = useState('');
    const [questionType, setQuestionType] = useState<'multiple-choice' | 'fill-in-blank' | 'essay' | 'short-answer'>('multiple-choice');
    const [correctAnswer, setCorrectAnswer] = useState('');
    const [explanation, setExplanation] = useState('');
    const [points, setPoints] = useState(2);
    const [difficulty, setDifficulty] = useState<1 | 2 | 3 | 4 | 5>(1);
    const [choices, setChoices] = useState<Choice[]>([
        { id: 'A', text: '' },
        { id: 'B', text: '' },
        { id: 'C', text: '' },
        { id: 'D', text: '' }
    ]);
    const [wordLimitMin, setWordLimitMin] = useState(0);
    const [wordLimitMax, setWordLimitMax] = useState(0);
    const [evaluationCriteria, setEvaluationCriteria] = useState('');
    const [sampleAnswer, setSampleAnswer] = useState('');
    const [audioUrl, setAudioUrl] = useState('');
    const [audioScript, setAudioScript] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [audioRegenerated, setAudioRegenerated] = useState(false);

    const getNextQuestionId = useCallback(() => {
        let maxId = 0;
        const sectionGroups = getSectionQuestionGroups();

        switch (section) {
            case 'listening': maxId = 0;   break;
            case 'reading':   maxId = 50;  break;
            case 'writing':   maxId = 100; break;
        }

        sectionGroups.forEach(group => {
            group.questions.forEach(q => {
                if (q.questionid > maxId) { maxId = q.questionid; }
            });
        });

        return maxId + 1;
    }, [getSectionQuestionGroups, section]);

    // Reset all states when dialog opens or question changes
    useEffect(() => {
        if (!isOpen) { return; }

        // Always reset error and audio regeneration status
        setError('');
        setAudioRegenerated(false);

        if (isCreatingNew) {
            // Creating new question - reset to defaults
            setQuestionId(getNextQuestionId());
            setQuestionText('');
            setQuestionImage('');
            setQuestionType('multiple-choice');
            setCorrectAnswer('');
            setExplanation('');
            setPoints(2);
            setDifficulty(1);
            setChoices([
                { id: 'A', text: '' },
                { id: 'B', text: '' },
                { id: 'C', text: '' },
                { id: 'D', text: '' }
            ]);
            setWordLimitMin(0);
            setWordLimitMax(0);
            setEvaluationCriteria('');
            setSampleAnswer('');
            setAudioUrl('');
            setAudioScript('');
        } else if (question) {
            // Editing existing question - populate from question data
            setQuestionId(question.questionid);
            setQuestionText(question.text || '');
            setQuestionImage(question.image || '');
            setQuestionType(question.type);
            setCorrectAnswer(question.correct_answer || '');
            setExplanation(question.explanation || '');
            setPoints(question.points);
            setDifficulty(question.difficulty_level);

            // Always reset choices (use question data or defaults)
            if (question.type === 'multiple-choice' && 'choices' in question) {
                setChoices(question.choices || [
                    { id: 'A', text: '' },
                    { id: 'B', text: '' },
                    { id: 'C', text: '' },
                    { id: 'D', text: '' }
                ]);
            } else {
                setChoices([
                    { id: 'A', text: '' },
                    { id: 'B', text: '' },
                    { id: 'C', text: '' },
                    { id: 'D', text: '' }
                ]);
            }

            // Always reset essay fields (use question data or defaults)
            if (question.type === 'essay' && 'word_limit' in question) {
                setWordLimitMin(question.word_limit?.min || 0);
                setWordLimitMax(question.word_limit?.max || 0);
                setEvaluationCriteria(question.evaluation_criteria?.join('\n') || '');
                setSampleAnswer(question.sample_answer || '');
            } else {
                setWordLimitMin(0);
                setWordLimitMax(0);
                setEvaluationCriteria('');
                setSampleAnswer('');
            }

            // Always reset audio fields
            if (question.audio) {
                setAudioUrl(question.audio.audio_url || '');
                setAudioScript(
                    question.audio.audio_script && Array.isArray(question.audio.audio_script)
                        ? JSON.stringify(question.audio.audio_script, null, 2)
                        : ''
                );
            } else {
                setAudioUrl('');
                setAudioScript('');
            }
        }
    }, [isOpen, question, isCreatingNew, getNextQuestionId]);

    const getGroupForQuestionId = (qId: number): string => {
        return questionIdToGroupMapping[qId] || '';
    };

    const handleRegenerateAudio = () => {
        if (!examid || !question) { return; }
        regenerateQuestionAudio({ examid, questionid: question.questionid });
        setAudioRegenerated(true);
    };

    const handleChoiceChange = (index: number, field: 'text' | 'image_url', value: string) => {
        const newChoices = [...choices];
        newChoices[index] = { ...newChoices[index], [field]: value };
        setChoices(newChoices);
    };

    const handleSave = async () => {
        if (isCreatingNew && (!correctAnswer || !explanation)) {
            setError('정답과 해설을 입력해주세요.');
            return;
        }

        if (isCreatingNew) {
            const validRanges = {
                'listening': { min: 1, max: 50 },
                'reading':   { min: 51, max: 100 },
                'writing':   { min: 101, max: 104 }
            };

            const range = validRanges[section];
            if (range && (questionId < range.min || questionId > range.max)) {
                setError(`유효하지 않은 문제 ID입니다. ${section} 섹션은 ${range.min}~${range.max} 범위입니다.`);
                return;
            }

            const allGroups = getSectionQuestionGroups();
            const exists = allGroups.some(group =>
                group.questions.some(q => q.questionid === questionId)
            );

            if (exists) {
                setError(`문제 ID ${questionId}이(가) 이미 존재합니다.`);
                return;
            }
        }

        setSaving(true);
        try {
            const updateData: Parameters<typeof updateQuestion>[0] = {
                examid: examid,
                questionid: isCreatingNew ? questionId : question!.questionid,
                text: questionText,
                image: questionImage || undefined,
                type: questionType,
                correct_answer: correctAnswer,
                explanation: explanation,
                points: points,
                difficulty_level: difficulty
            };

            if (audioUrl) {
                updateData.audio = {
                    type: 'audio',
                    audio_url: audioUrl,
                    audio_script: audioScript ? JSON.parse(audioScript) : []
                };
            } else {
                updateData.audio = null;
            }

            if (questionType === 'multiple-choice') {
                updateData.choices = choices;
            }

            if (questionType === 'essay') {
                updateData.essay_detail = {
                    word_limit_min: wordLimitMin,
                    word_limit_max: wordLimitMax,
                    evaluation_criteria: evaluationCriteria.split('\n').filter(c => c.trim()),
                    sample_answer: sampleAnswer
                };
            }

            const result = await updateQuestion(updateData);

            if (result) {
                onClose();
                onSave();
            } else {
                setError('문제 저장에 실패했습니다.');
            }
        } catch (err: any) {
            setError(err.message || '문제 저장 중 오류가 발생했습니다.');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) { return null; }

    return (
        <>
            <div className="ModalOverlay active" onClick={onClose}></div>
            <div className="Modal active" onClick={(e) => e.stopPropagation()}>
                <div className="ModalContent">
                    <div className="ModalHeader">
                        <h2 className="ModalTitle">
                            {isCreatingNew ? '새 문제 추가' : `문제 ${question?.questionid} 수정`}
                        </h2>
                    </div>
                    <div className="ModalBody">
                        {error && <div className="Error">{error}</div>}

                        <div className="FormGrid">
                            {isCreatingNew && (
                                <>
                                    <div className="InputGroup">
                                        <label className="InputLabel">문제 ID</label>
                                        <input
                                            type="number"
                                            className="Input"
                                            value={questionId}
                                            onChange={(e) => setQuestionId(parseInt(e.target.value) || 1)}
                                            min={1}
                                            max={104}
                                        />
                                        <span className="InputHelper">
                                            {section === 'listening' ? '1-50' : section === 'reading' ? '51-100' : '101-104'} 범위
                                        </span>
                                    </div>
                                    <div className="InputGroup">
                                        <label className="InputLabel">그룹</label>
                                        <input
                                            type="text"
                                            className="Input"
                                            value={getGroupForQuestionId(questionId) || '유효하지 않은 ID'}
                                            disabled
                                        />
                                    </div>
                                </>
                            )}

                            <div className="InputGroup full">
                                <label className="InputLabel">문제 텍스트</label>
                                <textarea
                                    className="Textarea"
                                    value={questionText}
                                    onChange={(e) => setQuestionText(e.target.value)}
                                    rows={3}
                                />
                            </div>

                            <div className="InputGroup full">
                                <label className="InputLabel">문제 이미지</label>
                                <EditableFileField
                                    value={questionImage || null}
                                    onChange={(url) => setQuestionImage(url || '')}
                                    accept="image/jpeg,image/png,image/gif,image/svg+xml"
                                    className="QuestionImageField"
                                    placeholder={<span className="QuestionImagePlaceholder">+ 이미지 업로드</span>}
                                />
                            </div>

                            {!!audioUrl && (
                                <>
                                    <div className="InputGroup full">
                                        <label className="InputLabel">오디오 URL</label>
                                        {audioUrl && (
                                            <div className="AudioPreview">
                                                <audio controls>
                                                    <source src={audioUrl} type="audio/mpeg" />
                                                </audio>
                                                {!isCreatingNew && (
                                                    <button
                                                        className={`RegenerateButton ${audioRegenerated ? 'success' : ''}`}
                                                        onClick={handleRegenerateAudio}
                                                    >
                                                        {audioRegenerated ? '요청됨' : '오디오 재생성'}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="InputGroup full">
                                        <label className="InputLabel">오디오 스크립트 (JSON)</label>
                                        <textarea
                                            className="Textarea"
                                            value={audioScript}
                                            onChange={(e) => setAudioScript(e.target.value)}
                                            rows={4}
                                            placeholder='[{"role": "speaker1", "text": "안녕하세요"}]'
                                        />
                                    </div>
                                </>
                            )}

                            <div className="FormRow">
                                <div className="InputGroup">
                                    <label className="InputLabel">문제 유형</label>
                                    <select
                                        className="Select"
                                        value={questionType}
                                        onChange={(e) => setQuestionType(e.target.value as typeof questionType)}
                                    >
                                        <option value="multiple-choice">객관식</option>
                                        <option value="fill-in-blank">빈칸 채우기</option>
                                        <option value="essay">에세이</option>
                                        <option value="short-answer">단답형</option>
                                    </select>
                                </div>
                                <div className="InputGroup">
                                    <label className="InputLabel">배점</label>
                                    <input
                                        type="number"
                                        className="Input"
                                        value={points}
                                        onChange={(e) => setPoints(parseInt(e.target.value) || 0)}
                                    />
                                </div>
                                <div className="InputGroup">
                                    <label className="InputLabel">난이도</label>
                                    <select
                                        className="Select"
                                        value={difficulty}
                                        onChange={(e) => setDifficulty(parseInt(e.target.value) as typeof difficulty)}
                                    >
                                        <option value={1}>1</option>
                                        <option value={2}>2</option>
                                        <option value={3}>3</option>
                                        <option value={4}>4</option>
                                        <option value={5}>5</option>
                                    </select>
                                </div>
                            </div>

                            {questionType === 'multiple-choice' && (
                                <div className="ChoicesSection">
                                    <div className="ChoicesTitle">보기</div>
                                    {(['A', 'B', 'C', 'D'] as const).map((id, index) => (
                                        <div key={id} className="ChoiceItem">
                                            <div className="ChoiceRow">
                                                <span className="ChoiceLabel">{id}.</span>
                                                <input
                                                    type="text"
                                                    className="Input"
                                                    value={choices[index]?.text || ''}
                                                    onChange={(e) => handleChoiceChange(index, 'text', e.target.value)}
                                                    placeholder={`보기 ${id} 텍스트`}
                                                />
                                            </div>
                                            <div className="ChoiceImageRow">
                                                <EditableFileField
                                                    value={choices[index]?.image_url || null}
                                                    onChange={(url) => handleChoiceChange(index, 'image_url', url || '')}
                                                    accept="image/jpeg,image/png,image/gif,image/svg+xml"
                                                    className="ChoiceImageField"
                                                    placeholder={<span className="ChoiceImagePlaceholder">+ 이미지</span>}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {(questionType === 'essay' || questionType === 'short-answer') && (
                                <div className="FormRow">
                                    <div className="InputGroup">
                                        <label className="InputLabel">최소 글자수</label>
                                        <input
                                            type="number"
                                            className="Input"
                                            value={wordLimitMin}
                                            onChange={(e) => setWordLimitMin(parseInt(e.target.value) || 0)}
                                        />
                                    </div>
                                    <div className="InputGroup">
                                        <label className="InputLabel">최대 글자수</label>
                                        <input
                                            type="number"
                                            className="Input"
                                            value={wordLimitMax}
                                            onChange={(e) => setWordLimitMax(parseInt(e.target.value) || 0)}
                                        />
                                    </div>
                                </div>
                            )}

                            {questionType === 'essay' && (
                                <>
                                    <div className="InputGroup full">
                                        <label className="InputLabel">평가 기준 (줄바꿈으로 구분)</label>
                                        <textarea
                                            className="Textarea"
                                            value={evaluationCriteria}
                                            onChange={(e) => setEvaluationCriteria(e.target.value)}
                                            rows={3}
                                        />
                                    </div>
                                    <div className="InputGroup full">
                                        <label className="InputLabel">모범 답안</label>
                                        <textarea
                                            className="Textarea"
                                            value={sampleAnswer}
                                            onChange={(e) => setSampleAnswer(e.target.value)}
                                            rows={4}
                                        />
                                    </div>
                                </>
                            )}

                            <div className="InputGroup full">
                                <label className="InputLabel">정답</label>
                                <input
                                    type="text"
                                    className="Input"
                                    value={correctAnswer}
                                    onChange={(e) => setCorrectAnswer(e.target.value)}
                                    placeholder={questionType === 'multiple-choice' ? 'A, B, C, D 중 하나' : '정답 입력'}
                                />
                            </div>

                            <div className="InputGroup full">
                                <label className="InputLabel">해설</label>
                                <textarea
                                    className="Textarea"
                                    value={explanation}
                                    onChange={(e) => setExplanation(e.target.value)}
                                    rows={3}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="ModalFooter">
                        <button className="ModalButton TertiaryButton" onClick={onClose}>취소</button>
                        <button
                            className="ModalButton PrimaryButton"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? '저장 중...' : '저장'}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}

export default EditQuestionDialog;
